import * as Sentry from '@sentry/react'
import { setUserId } from '@snowplow/browser-tracker'
import {
  AuthError,
  createClient,
  FunctionsHttpError,
  Session,
  User,
} from '@supabase/supabase-js'
import i18n from '../../../translations/i18n'
import {
  CancelRevenueCatSubscriptionsData,
  CreateStripeInvoiceData,
  CreateStripeSubscriptionData,
  CreateStripeTrialSubscriptionData,
  EdgeFunctionError,
  EventName,
  GetStripePaymentMethodTypeResponse,
  RefundRevenueCatSubscriptionsData,
  StripePrice,
} from '../types'
import { getConfidenceVisitorId } from './localStorage'
import { startWebConfidenceVisitorIdentitySync } from './mergeWebConfidenceVisitorIdentity'
import { getUserCountryCode } from './sessionStorage'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

const AUTH_LOCK_RETRY_ATTEMPTS = 3

// Backs off 250ms then 500ms. Sized for the worst case this exists to survive —
// returning to an in-app browser after a PayPal round-trip, where the lock is
// held for a token refresh over a slow connection — because completePaymentLoader
// runs after the payment has already succeeded server-side and must not drop the
// customer on the error page over a transient hiccup.
const AUTH_LOCK_RETRY_DELAY_MS = 250

export const supabase = createClient(supabaseUrl, supabaseKey)

function isAuthLockError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const authLockError = error as {
    isAcquireTimeout?: boolean
    message?: string
  }

  return (
    authLockError.isAcquireTimeout === true ||
    (typeof authLockError.message === 'string' &&
      authLockError.message.includes('stole it'))
  )
}

/**
 * Retries an auth call that lost the token lock.
 *
 * supabase-js serializes all access to the auth token behind a browser Web Lock
 * (`lock:sb-<ref>-auth-token`), so a concurrent auth call — typically the
 * client's own focus-triggered token refresh — can steal it and make this one
 * reject.
 *
 * The only retry for these calls. Callers must not add their own: this is
 * wrapped around `getUser` and `getSession` themselves, so an outer loop
 * multiplies rather than extends it — a caller retrying three times around a
 * helper that already retries three times issues nine requests, not three.
 */
async function withAuthLockRetry<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt < AUTH_LOCK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      if (!isAuthLockError(error) || attempt === AUTH_LOCK_RETRY_ATTEMPTS - 1) {
        throw error
      }

      // A breadcrumb rather than an exception: the retry usually succeeds, so
      // reporting it would be noise, but without it a failure that exhausts
      // every attempt arrives with no sign of what preceded it.
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: `Retrying auth call after token lock error (attempt ${
          attempt + 1
        })`,
      })

      await new Promise((resolve) =>
        setTimeout(resolve, AUTH_LOCK_RETRY_DELAY_MS * (attempt + 1)),
      )
    }
  }

  throw lastError
}

// Registered next to the client that emits the auth events, so every app in the
// monorepo is covered without wiring this into each of the entry points.
startWebConfidenceVisitorIdentitySync()

export async function cancelRevenueCatSubscriptions(): Promise<CancelRevenueCatSubscriptionsData> {
  const response = await supabase.functions.invoke(
    'cancel-revenuecat-subscriptions',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      },
    },
  )

  const data = response.data as CancelRevenueCatSubscriptionsData
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function createStripeInvoice({
  currency,
  email,
  priceId,
}: {
  currency: string
  email: string | undefined
  priceId: string
}) {
  const response = await supabase.functions.invoke('create-stripe-invoice', {
    body: {
      currency,
      email,
      priceId,
      appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
    },
  })

  const data = response.data as CreateStripeInvoiceData

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return { data }
}

export async function createStripeSubscription({
  currency,
  priceId,
  trialPeriodDays,
}: {
  currency: string
  priceId: string
  trialPeriodDays?: number
}) {
  const response = await supabase.functions.invoke(
    'create-stripe-subscription',
    {
      body: {
        currency,
        priceId,
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
        trialPeriodDays,
      },
    },
  )

  const data = response.data as CreateStripeSubscriptionData

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return { data }
}

export async function createStripeTrialSubscription({
  setupIntentId,
}: {
  setupIntentId: string
}) {
  const response = await supabase.functions.invoke(
    'create-stripe-trial-subscription',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
        setupIntentId,
      },
    },
  )

  const data = response.data as CreateStripeTrialSubscriptionData

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return { data }
}

export async function scheduleRevenueCatEmailUpdate() {
  const response = await supabase.functions.invoke(
    'schedule-revenuecat-email-update',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      },
    },
  )

  const error = response.error as EdgeFunctionError

  if (error) throw error
}

export async function getUser(): Promise<{
  data: { user: User | null }
  error: AuthError | null
}> {
  return withAuthLockRetry(() => supabase.auth.getUser())
}

export async function getSession(): Promise<{
  data: { session: Session | null }
  error: AuthError | null
}> {
  return withAuthLockRetry(() => supabase.auth.getSession())
}

export async function getStripePaymentMethodType(
  paymentMethodId: string,
): Promise<GetStripePaymentMethodTypeResponse> {
  const response = await supabase.functions.invoke(
    'get-stripe-payment-method-type',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
        paymentMethodId,
      },
    },
  )

  const data = response.data as GetStripePaymentMethodTypeResponse
  const error = response.error as EdgeFunctionError

  if (error) Sentry.captureException(error)

  return data
}

// The request is identical on every call — `appId` and `productId` are build
// time constants — and prices don't change under a visitor mid-session, so the
// invocation is shared instead of repeated. This matters because the plans
// loader awaits it on a cold call before it can paint anything.
let stripePricesPromise: Promise<StripePrice[]> | undefined

/**
 * Warms the price cache without blocking. Call this from a screen the visitor
 * is already waiting on, so the plans loader can resolve from memory rather
 * than spending a round trip once they arrive.
 */
export function prefetchStripePrices(): void {
  // Swallowed here only to avoid an unhandled rejection from a call nobody is
  // awaiting. The failure is not cached, so the real caller retries and
  // surfaces the error itself.
  void listStripePrices().catch(() => undefined)
}

export async function listStripePrices(): Promise<StripePrice[]> {
  if (!stripePricesPromise) {
    stripePricesPromise = invokeListStripePrices()

    // A rejected invocation must not stay cached, otherwise one transient
    // failure would pin every later caller to the same error for the session.
    void stripePricesPromise.catch(() => {
      stripePricesPromise = undefined
    })
  }

  return stripePricesPromise
}

async function invokeListStripePrices(): Promise<StripePrice[]> {
  const response = await supabase.functions.invoke('list-stripe-prices', {
    body: {
      appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      productId: import.meta.env.VITE_STRIPE_PRODUCT_ID as string,
    },
  })

  const data = response.data as StripePrice[]
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function refundRevenueCatSubscriptions(): Promise<RefundRevenueCatSubscriptionsData> {
  const response = await supabase.functions.invoke(
    'refund-revenuecat-subscriptions',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      },
    },
  )

  const data = response.data as RefundRevenueCatSubscriptionsData
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function resetPasswordForEmail({ email }: { email: string }) {
  const { error } = await supabase.functions.invoke(
    'send-password-recovery-email',
    {
      body: {
        appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
        originalEmail: email,
      },
    },
  )

  let isEmailNotFound = false
  let isRateLimited = false

  if (error instanceof FunctionsHttpError) {
    if (error.context?.status === 429) {
      isRateLimited = true
    } else {
      try {
        const errorData = await error.context?.json()
        const errorMessage =
          typeof errorData?.error === 'string'
            ? errorData.error.toLowerCase()
            : ''
        isEmailNotFound =
          error.context?.status === 404 ||
          errorData?.error === 'EMAIL_NOT_FOUND' ||
          errorMessage.includes('email not found')
      } catch {
        isEmailNotFound = error.context?.status === 404
      }
    }
  }

  if (error && !isEmailNotFound && !isRateLimited) {
    Sentry.captureException(error)
  }

  return { error, isEmailNotFound, isRateLimited }
}

export async function trackEvent({
  appId,
  currency,
  eventId,
  eventName,
  eventSourceUrl,
  fbc,
  fbcFromCookie,
  fbcFromLocalStorage,
  fbp,
  pixelId,
  userCity,
  userCountryCode,
  userEmail,
  userId,
  userZipCode,
  value,
}: {
  appId: string
  currency?: string
  eventId: string
  eventName: EventName
  eventSourceUrl: string
  fbc: string | undefined | null
  fbcFromCookie: string | undefined | null
  fbcFromLocalStorage: string | undefined | null
  fbp: string | undefined
  pixelId: string
  userCity: string | undefined
  userCountryCode: string | undefined
  userEmail: string | undefined
  userId: string | undefined
  userZipCode: string | undefined
  value?: number
}) {
  const response = await supabase.functions.invoke('track-facebook-event', {
    body: {
      appId,
      currency,
      eventId,
      eventName,
      eventSourceUrl,
      fbc,
      fbcFromCookie,
      fbcFromLocalStorage,
      fbp,
      pixelId,
      userCity,
      userCountryCode,
      userEmail,
      userId,
      userZipCode,
      value,
    },
  })

  const error = response.error as EdgeFunctionError

  if (error) Sentry.captureException(error)
}

export async function signInWithPassword({
  email,
  password,
}: {
  email: string
  password: string
}) {
  const emailWithPrefix = `${import.meta.env.VITE_SUPABASE_APP_ID}_${email}`

  const { data, error } = await supabase.auth.signInWithPassword({
    email: emailWithPrefix,
    password,
  })

  if (error) Sentry.captureException(error)

  if (data.user?.id) {
    setUserId(data.user.id)
  }

  return { data, error }
}

export async function generateMagicLink({
  redirectTo,
}: {
  redirectTo: string
}) {
  const response = await supabase.functions.invoke('generate-magic-link', {
    body: {
      appId: import.meta.env.VITE_SUPABASE_APP_ID as string,
      redirectTo,
    },
  })

  const data = response.data as { magicLinkUrl: string }
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function signUp({
  email,
  password,
}: {
  email: string
  password: string
}) {
  const appId = import.meta.env.VITE_SUPABASE_APP_ID as string
  const emailWithPrefix = `${appId}_${email}`

  const confidenceVisitorId = getConfidenceVisitorId()

  // Written straight into the signup metadata instead of through
  // mergeWebConfidenceVisitorIdentity: signup creates the account, so there is
  // no concurrent writer to merge with, and with email confirmation enabled
  // there may be no session yet for the RPC to authenticate against. Only a
  // trusted id is stored — the "unknown" placeholder is dropped.
  const confidenceVisitorIdentity =
    confidenceVisitorId && confidenceVisitorId !== 'unknown'
      ? {
          confidence_visitor_ids: [confidenceVisitorId],
          original_confidence_visitor_id: confidenceVisitorId,
        }
      : {}

  const options = {
    data: {
      app_id: appId,
      ...confidenceVisitorIdentity,
      country: await getUserCountryCode(),
      language: i18n.language,
      original_email: email,
      sign_up_platform: 'web',
    },
  }

  const { error, data } = await supabase.auth.signUp({
    email: emailWithPrefix,
    password,
    options: options,
  })

  if (error) Sentry.captureException(error)

  if (data.user?.id) {
    setUserId(data.user.id)
  }

  return { error }
}

export async function grantRevenueCatEntitlement({
  paymentIntentId,
}: {
  paymentIntentId: string
}) {
  const response = await supabase.functions.invoke(
    'grant-revenuecat-entitlement',
    {
      body: { paymentIntentId },
    },
  )

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return response.data
}

export async function updatePassword({ password }: { password: string }) {
  const { error } = await supabase.auth.updateUser({
    password,
  })

  if (error) Sentry.captureException(error)

  return { error }
}
